import React, { useMemo } from 'react';
import type { Dispatch } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { TaskSpaces } from '../tasksDefinitions';

import { CheckboxSelectFilter } from './CheckboxSelectFilter';
import { FilterKeys } from './filtersDefinitions';
import type { FiltersState } from './useFiltersReducer';

const i18n = defineMessages({
	spaceLabel: {
		id: 'home.views.tasks.filters.label.space',
		defaultMessage: 'Space',
		description:
			'The label for a selector to select a space from a list of different confluence spaces.',
	},
});

interface SelectSpaceProps {
	options: TaskSpaces;
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const SelectSpace = ({ options, filterDispatch, filterState }: SelectSpaceProps) => {
	const { formatMessage } = useIntl();

	const checkboxOptions = useMemo(
		() =>
			Object.keys(options).map((spaceId: string) => ({
				label: options?.[spaceId]?.name ?? ('' as string),
				value: options?.[spaceId]?.spaceId ?? ('' as string),
			})),
		[options],
	);

	const getSelectedValue = () =>
		// Using the filter values from state, match value from options formatted above.
		(filterState.filters[FilterKeys.SPACE] ?? []).reduce(
			(current, stateSpaceId) => {
				const match = checkboxOptions.find(
					(checkboxOption) => checkboxOption.value === stateSpaceId,
				);

				if (match) {
					current.push(match);
				}

				return current;
			},
			[] as { label: string; value: string }[],
		);

	return (
		<CheckboxSelectFilter
			options={checkboxOptions}
			filterKey={FilterKeys.SPACE}
			label={formatMessage(i18n.spaceLabel)}
			inputId="space-filter"
			filterDispatch={filterDispatch}
			selectedValues={getSelectedValue()}
		/>
	);
};
